import React, { useEffect } from "react";
import Nav from "../../components/Nav/Nav";

import {ManagerContext, UserContext} from "../../App";
import { Button, Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, TextField, Box, Alert, Paper, CircularProgress, Chip } from "@mui/material";

import './ManagerHomeView.scss';
import { Link, useNavigate } from "react-router-dom";
import { ContextTicketDto } from "../HomeView/HomeView";
import ManagetNav from "../../components/ManagerNav/ManagerNav";
import WeekPreview from "../../components/WeekPreview/WeekPreview";
import CourtEventApi, { CourtEventDto } from "../../api/CourtEventApi";
import { DateTime } from 'luxon';
import { deriveStageLabel, isClosedStage, noStatusNeedsAction } from "../../utils/caseStage";
import { clientDisplayName } from "../../utils/clientName";

export const ticketStatusNames: TicketStatusNameType[] = [
  'citation received (Approving)',
  'case accepted (Approved)',
  'letter of representation sent (Active)',
  'court respond by date satisfied (Pending)',
  'New court date',
  'negotiating with prosecutor',
  'offer received',
  'waiting for client to reject or accept offer',
  'waiting for judge to approve',
  'deferral/DSC conditions',
  'case closed/dismissed (closed)'
];
export type TicketStatusNameType = 
  'citation received (Approving)' 
  | 'case accepted (Approved)' 
  | 'letter of representation sent (Active)' 
  | 'court respond by date satisfied (Pending)' 
  | 'New court date' 
  | 'negotiating with prosecutor' 
  | 'offer received' 
  | 'waiting for client to reject or accept offer' 
  | 'waiting for judge to approve'
  | 'deferral/DSC conditions'
  | 'case closed/dismissed (closed)';

// Statuses where staff must do something next; drives the "Cases Needing Action" list.
// Cases with no status yet (brand-new intake) are always treated as needing action.
const ACTION_STATUSES = new Set<string>([
  'court respond by date satisfied (Pending)',
  'New court date',
  'offer received',
  'waiting for client to reject or accept offer',
  'waiting for judge to approve',
]);

// Phase 4 (G.14) — stale-case flagging.
// A case is "stale" when it has quietly stalled and would otherwise look like it's just waiting:
//   • no-ack       — the LOR was sent but the court never acknowledged it, past the follow-up window
//   • no-court-date — the LOR was acknowledged but there is no upcoming court date on file
// LOR follow-ups escalate through day 3 / 7 / 10; we treat "past the window" as 10+ days unconfirmed.
const STALE_LOR_UNCONFIRMED_DAYS = 10;
// A case opened this long ago that still has no LOR activity at all is "going cold" — this catches
// older Clio-imported cases that never got LOR flags (Lloyd, Jul 8: cases slipping through). New
// imports now get age-based LOR flags on import, so this mainly surfaces the pre-existing backlog.
const STALE_COLD_NO_ACTIVITY_DAYS = 21;

type StaleReason = 'no-ack' | 'no-court-date' | 'going-cold';

const daysSince = (iso?: string | null): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return (Date.now() - t) / (1000 * 60 * 60 * 24);
};

const getStaleReason = (ct: ContextTicketDto): StaleReason | null => {
  const t = ct.ticket;

  // Non-traffic matters are never stale by these rules (Lloyd, Aug 6). All three reasons below are
  // about a letter of representation the court never acknowledged, or a court date that follows
  // from one. A criminal matter has no LOR, so every one of them would report "going cold" forever
  // and bury the traffic cases this list exists to surface.
  if (t.Ticket_MatterType === 'NON_TRAFFIC') return null;

  const confirmed = Boolean(t.Ticket_LorConfirmedAt);

  // 1) LOR sent but never acknowledged, past the day-10 follow-up window.
  if (t.Ticket_LorSent && !confirmed) {
    const age = daysSince(t.Ticket_LorSentAt);
    if (age !== null && age >= STALE_LOR_UNCONFIRMED_DAYS) return 'no-ack';
  }

  // 2) Acknowledged, but no upcoming court date scheduled.
  if (confirmed) {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const courtDate = t.Ticket_CourtDate ? new Date(t.Ticket_CourtDate) : null;
    const hasUpcomingCourtDate =
      courtDate !== null &&
      !Number.isNaN(courtDate.getTime()) &&
      courtDate.getTime() >= startOfToday.getTime();
    if (!hasUpcomingCourtDate) return 'no-court-date';
  }

  // 3) Going cold — opened weeks ago but no LOR activity at all. Catches older imported cases that
  // never got LOR flags (so they'd otherwise never appear on the LOR-based stale checks above).
  // Skip closed cases.
  if (!t.Ticket_LorSent && !confirmed && !isClosedStage(t, ct.ticketStatus?.TicketStatus_Name)) {
    const age = daysSince(t.Ticket_CreatedAt);
    if (age !== null && age >= STALE_COLD_NO_ACTIVITY_DAYS) return 'going-cold';
  }

  return null;
};

const STALE_REASON_LABEL: Record<StaleReason, string> = {
  'no-ack': 'No LOR acknowledgment',
  'no-court-date': 'No upcoming court date',
  'going-cold': 'Going cold — no recent activity',
};


const ManagerHomeView = () => {
  const manager = React.useContext(ManagerContext);
  const navigate = useNavigate();

  const [activeTickets, setActiveTickets] = React.useState<ContextTicketDto[]>([]);
  const [ticketsLoading, setTicketsLoading] = React.useState(true);
  const [clientSearch, setClientSearch] = React.useState('');

  const handleClientSearch = () => {
    const term = clientSearch.trim();
    navigate(term ? `/users?lastName=${encodeURIComponent(term)}` : '/users');
  };

  
  // State for court events
  const [courtEvents, setCourtEvents] = React.useState<CourtEventDto[]>([]);
  const [eventsLoading, setEventsLoading] = React.useState(true);
  const [eventsError, setEventsError] = React.useState<string | null>(null);

  const handleTicketClick = (ticketGuid: string) => {
    navigate(`/ticket?Ticket_guid=${ticketGuid}`);
  };

  useEffect(() => {
    // Fetch active tickets when the component mounts
    getActiveTickets();
    // Load court events for this week
    loadWeekCourtEvents();
  }, []);

  const loadWeekCourtEvents = async () => {
    try {
      setEventsLoading(true);
      setEventsError(null);

      // Get court events for this week (7 days from today)
      const now = DateTime.now().startOf('week'); // Start of current week
      const endOfWeek = now.endOf('week'); // End of current week
      
      const events = await CourtEventApi.listCourtEvents({
        CourtEvent_Start_From: now.toMillis(),
        CourtEvent_Start_To: endOfWeek.toMillis()
      });

      setCourtEvents(events);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load court events';
      setEventsError(errorMessage);
      console.error('Error loading court events:', err);
    } finally {
      setEventsLoading(false);
    }
  };
  
  const getActiveTickets = async () => {
    setTicketsLoading(true);
    try {
      const response = await fetch((process.env.API_HOST||'') + '/api/v1/tickets?size=2000&Ticket_Active=true', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to fetch tickets');
      }
      const tickets = await response.json();
      setActiveTickets(tickets);
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setTicketsLoading(false);
    }
  }

  const needsAction = activeTickets.filter((ct) => {
    const s = ct.ticketStatus?.TicketStatus_Name;
    // No status row (e.g. imported cases): only genuinely-new cases (hired, LOR not yet sent)
    // need action. Imported cases whose LOR is already sent are NOT pending — this is what
    // collapses the old inflated "~50 approving" list down to the true handful (Lloyd, Jul 8).
    if (!s) return noStatusNeedsAction(ct.ticket);
    return ACTION_STATUSES.has(s);
  });

  // Render the mapped 8-stage label for a case instead of the raw internal status / legacy
  // "approving" wording, so the dashboard tracks Lloyd's 8 stages (Jul 8 feedback).
  const renderStageChip = (ct: ContextTicketDto) => {
    const statusName = ct.ticketStatus?.TicketStatus_Name;
    const closed = isClosedStage(ct.ticket, statusName);
    const color = closed ? 'default' : !statusName ? 'warning' : 'info';
    return (
      <Chip label={deriveStageLabel(ct.ticket, statusName)} color={color} size="small" />
    );
  };

  // Stale cases among the loaded active cases (G.14).
  const staleCases = activeTickets
    .map((ct) => ({ ct, reason: getStaleReason(ct) }))
    .filter((x): x is { ct: ContextTicketDto; reason: StaleReason } => x.reason !== null);

  return (
    <>
      <ManagetNav></ManagetNav>
      <div className="mahomev">
        <Container className="mahomev-ctn">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 2 }}>
            <Typography variant="h3" className="mahomev-ctn-ttl">Welcome {manager?.Manager_FirstName}</Typography>
          </Box>

          {/* Client search */}
          <Paper elevation={1} sx={{ p: 2, mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              label="Search clients by last name"
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleClientSearch(); }}
            />
            <Button variant="contained" onClick={handleClientSearch}>
              Search
            </Button>
          </Paper>

          {/* Week Preview Section */}
          <Box sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              This Week's Court Events
            </Typography>
            {eventsLoading ? (
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Loading court events...
                </Typography>
              </Paper>
            ) : eventsError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to load court events: {eventsError}
              </Alert>
            ) : courtEvents.length > 0 ? (
              <Paper elevation={1} sx={{ p: 2 }}>
                <WeekPreview 
                  courtEvents={courtEvents}
                  currentDate={new Date()}
                  minShownTime="07:00"
                  maxShownTime="19:00"
                />
              </Paper>
            ) : (
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No court events scheduled for this week
                </Typography>
              </Paper>
            )}
          </Box>
          


          {/* Cases Needing Action */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Cases Needing Action{ticketsLoading ? '' : ` (${needsAction.length})`}
            </Typography>
            {ticketsLoading ? (
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Loading cases...
                </Typography>
              </Paper>
            ) : needsAction.length === 0 ? (
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No cases need action right now.
                </Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Case #</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Court Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {needsAction.map((ct) => (
                      <TableRow
                        key={ct.ticket.Ticket_guid}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleTicketClick(ct.ticket.Ticket_guid)}
                      >
                        <TableCell>
                          <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium' }}>
                            {ct.ticket.Ticket_MatterNumber || ct.ticket.Ticket_Number || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {ct.user && clientDisplayName(ct.user)}
                        </TableCell>
                        <TableCell>{renderStageChip(ct)}</TableCell>
                        <TableCell>
                          {ct.ticket.Ticket_CourtDate
                            ? new Date(ct.ticket.Ticket_CourtDate).toLocaleDateString()
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          {/* Stale Cases (G.14) — cases that have quietly stalled */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
              Stale Cases{ticketsLoading ? '' : ` (${staleCases.length})`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Cases with no LOR acknowledgment past the follow-up window, acknowledged with no
              upcoming court date, or going cold (opened weeks ago with no LOR activity).
            </Typography>
            {ticketsLoading ? (
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Loading stale cases...
                </Typography>
              </Paper>
            ) : staleCases.length === 0 ? (
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No stale cases right now.
                </Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Case #</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Why stale</TableCell>
                      <TableCell>Court Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {staleCases.map(({ ct, reason }) => (
                      <TableRow
                        key={ct.ticket.Ticket_guid}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleTicketClick(ct.ticket.Ticket_guid)}
                      >
                        <TableCell>
                          <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium' }}>
                            {ct.ticket.Ticket_MatterNumber || ct.ticket.Ticket_Number || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {ct.user && clientDisplayName(ct.user)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={STALE_REASON_LABEL[reason]}
                            color="warning"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {ct.ticket.Ticket_CourtDate
                            ? new Date(ct.ticket.Ticket_CourtDate).toLocaleDateString()
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          {ticketsLoading ? (
            <>
              <br />
              <Typography variant="h5" className="">Your Active Tickets</Typography>
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Loading tickets...
                </Typography>
              </Paper>
            </>
          ) : activeTickets.length > 0 && (
            <>
              <br />
              <Typography variant="h5" className="">Your Active Tickets</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Ticket Number</TableCell>
                      <TableCell>Client Name</TableCell>
                      <TableCell>Citation</TableCell>
                      <TableCell>Representation</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Court Date</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeTickets.slice(0, 5).map((contextTicket) => (
                      <TableRow
                        key={contextTicket.ticket.Ticket_guid}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleTicketClick(contextTicket.ticket.Ticket_guid)}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            color="primary"
                            sx={{ fontWeight: 'medium' }}
                          >
                            {contextTicket.ticket.Ticket_Number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {contextTicket?.user && 
                            `${contextTicket.user.User_FirstName} ${contextTicket.user.User_LastName}`
                          }
                        </TableCell>
                        <TableCell>{contextTicket.ticket.Ticket_Citation}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                            {contextTicket.ticket.Ticket_Representation || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>{renderStageChip(contextTicket)}</TableCell>
                        <TableCell>
                          {contextTicket.ticket.Ticket_CourtDate
                            ? new Date(contextTicket.ticket.Ticket_CourtDate).toLocaleDateString()
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {new Date(contextTicket.ticket.Ticket_CreatedAt).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          <br />
        </Container>
      </div>
    </>
  );
}
export default ManagerHomeView;